import { useEffect } from 'react';
import { useTranslation, WithTranslation } from 'react-i18next';
import Markdown from 'shared/components/Markdown';
import { SPACINGS } from 'shared/styles/variables';
import styled from 'styled-components';

import Spacer from 'design-system/components/Spacer';
import IconCircleFilledCheck from 'images/icons/circle-filled-check.svg';
import IconCircleFilledX from 'images/icons/circle-filled-x.svg';
import IconCamera from 'images/icons/inquiry/camera.svg';
import Button, { ButtonType } from 'inquiry/components/Button';
import EventsClient from 'lib/clients/EventsClient';
import { SelfieCaptureOption, SelfieMatch } from 'lib/interfaces/verificationConfig';
import { captureMessage } from 'lib/reporting';
import ButtonInputCapture from 'verify/components/ButtonInputCapture';
import CenteredPictograph from 'verify/components/CenteredPictograph';
import CustomizablePictograph from 'verify/components/CustomizablePictograph';
import PositionableButtonContainer from 'verify/components/PositionableButtonContainer/PositionableButtonContainer';
import SelfiePictograph from 'verify/components/SelfiePictograph';
import ThemedParagraph from 'verify/components/ThemedParagraph';
import ThemedTitle from 'verify/components/ThemedTitle';
import PromptOptions from 'verify/lib/interfaces/PromptOptions';
import { SelfieStep } from 'verify/lib/interfaces/selfie';
import { MIN_HEIGHTS } from 'verify/styles/variables';

const Header = styled('div')`
  min-height: ${MIN_HEIGHTS.header};
  margin-bottom: ${SPACINGS.base * 4}px;
`;

const OptionPrimary = styled('div')`
  margin-bottom: ${SPACINGS.base * 2}px;
`;

const IconCameraStyled = styled(IconCamera)`
  path[stroke] {
    stroke: ${(props) => props.theme.colorFontButtonPrimary};
  }
`;

const CaptureOptionsContainer = styled.div`
  width: 100%;
`;

interface VerifySelfiePromptViewProps {
  handleSelectImage: (file: File) => void;
  handleStartRecording: () => Promise<void>;
  handleDeviceHandoff: () => void;
  enabledCaptureOptions: SelfieCaptureOption[];
  isProcessing: PromptOptions;
  currentStep: SelfieStep;
  requireSelfieMatch: SelfieMatch;
}

export default function VerifySelfiePromptView(props: VerifySelfiePromptViewProps) {
  const { enabledCaptureOptions } = props;
  const [t, i18n] = useTranslation();

  // Prefer the web camera recording over a stillfie
  if (enabledCaptureOptions.includes(SelfieCaptureOption.WebCamera)) {
    return <RecordView {...props} t={t} i18n={i18n} />;
  } else if (enabledCaptureOptions.includes(SelfieCaptureOption.Stillfie)) {
    return <StillfieView {...props} t={t} i18n={i18n} />;
  } else {
    // This should never happen, but fallback to stillfie and send Sentry warning so we can fix.
    captureMessage('Expected either web_camera or stillfie capture options', 'warning', {
      enabledCaptureOptions,
    });
    return <StillfieView {...props} t={t} i18n={i18n} />;
  }
}

type SubViewProps = VerifySelfiePromptViewProps & Omit<WithTranslation, 'tReady'>;

interface DeviceHandoffOptionProps {
  handleDeviceHandoff: () => void;
  isProcessing: PromptOptions;
}

function DeviceHandoffOption(props: DeviceHandoffOptionProps) {
  const { handleDeviceHandoff: handleDeviceHandoff, isProcessing } = props;

  const [t] = useTranslation();

  return (
    <Button
      id='selfie-prompt__button--device-handoff'
      data-test='selfie-prompt__button--device-handoff'
      buttonType={ButtonType.Secondary}
      disabled={!!isProcessing}
      isPending={isProcessing === PromptOptions.DeviceHandoff}
      onClick={handleDeviceHandoff}
    >
      {t('selfie.prompt.btn-continue-on-device')}
    </Button>
  );
}

function RecordView({
  t,
  enabledCaptureOptions,
  isProcessing,
  requireSelfieMatch,
  handleDeviceHandoff,
  handleStartRecording,
}: SubViewProps) {
  let prompt = '';
  let pictograph;

  switch (requireSelfieMatch) {
    case SelfieMatch.ProfileAndCenter:
      prompt = t('selfie.prompt.prompt');
      pictograph = (
        <CenteredPictograph height={120} width={120} aria-label='selfie pictograph'>
          <CustomizablePictograph
            url='selfiePictographUrl'
            height='selfiePictographHeight'
            aria-label='selfie pictograph'
            themed={false}
          >
            <SelfiePictograph frameDuration={100}>
              <SelfiePictograph.Center frames={8} />
              <SelfiePictograph.Left1 />
              <SelfiePictograph.Left2 frames={8} />
              <SelfiePictograph.Left1 />
              <SelfiePictograph.Center />
              <SelfiePictograph.Right1 />
              <SelfiePictograph.Right2 frames={8} />
              <SelfiePictograph.Right1 />
            </SelfiePictograph>
          </CustomizablePictograph>
        </CenteredPictograph>
      );
      break;

    case SelfieMatch.OnlyCenter:
      prompt = t('selfie.prompt.prompt-center');
      pictograph = (
        <CenteredPictograph height={120} width={120} aria-label='selfie center pictograph'>
          <CustomizablePictograph
            url='selfieCenterPictographUrl'
            height='selfieCenterPictographHeight'
            aria-label='selfie center pictograph'
            themed={false}
          >
            <SelfiePictograph>
              <SelfiePictograph.Center />
            </SelfiePictograph>
          </CustomizablePictograph>
        </CenteredPictograph>
      );
      break;

    default:
      captureMessage('Invalid selfie match requirement');
      break;
  }

  return (
    <>
      <Header>
        <ThemedTitle>
          <Markdown>{t('selfie.prompt.title')}</Markdown>
        </ThemedTitle>
        <ThemedParagraph>
          <Markdown>{prompt}</Markdown>
        </ThemedParagraph>
      </Header>

      {pictograph}

      <PositionableButtonContainer>
        <CaptureOptionsContainer>
          <OptionPrimary>
            <Button
              id='selfie-prompt__button--camera'
              buttonType={ButtonType.Primary}
              disabled={!!isProcessing}
              isPending={isProcessing === PromptOptions.SelectCamera}
              onClick={handleStartRecording}
            >
              {t('selfie.prompt.btn-submit')}
            </Button>
          </OptionPrimary>
          {enabledCaptureOptions.includes(SelfieCaptureOption.DeviceHandoff) && (
            <DeviceHandoffOption
              handleDeviceHandoff={handleDeviceHandoff}
              isProcessing={isProcessing}
            />
          )}
        </CaptureOptionsContainer>
      </PositionableButtonContainer>
    </>
  );
}

const StillfieHint = styled.div`
  display: flex;
  justify-content: center;
`;

const StillfieHintColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  width: 145px;

  font-size: ${({ theme }) => theme.fontSizeSmall};
  line-height: ${({ theme }) => theme.lineHeightSmall};
`;

const StillfieHintIconContainer = styled.div`
  position: absolute;
  top: 108px;
  right: -4px;
`;

const StillfieHintText = styled.p`
  width: 100px;
`;

function StillfieView({
  t,
  enabledCaptureOptions,
  isProcessing,
  currentStep,
  handleDeviceHandoff,
  handleSelectImage,
}: SubViewProps) {
  // We often get questions about whether someone saw the stillfie view or not so let's log it!
  useEffect(() => {
    EventsClient.logAmplitude('selfie.stillfie', { currentStep });
  }, [currentStep]);

  let prompt;
  let pictograph;

  switch (currentStep) {
    case SelfieStep.Center:
      prompt = t('selfie.prompt.prompt-center');
      pictograph = (
        <CenteredPictograph height={120} width={120} aria-label='stillfie center pictograph'>
          <CustomizablePictograph
            url='selfieCenterPictographUrl'
            height='selfieCenterPictographHeight'
            aria-label='stillfie center pictograph'
            themed={false}
          >
            <StillfieHint>
              <StillfieHintColumn>
                <SelfiePictograph>
                  <SelfiePictograph.Center />
                </SelfiePictograph>
              </StillfieHintColumn>
            </StillfieHint>
          </CustomizablePictograph>
        </CenteredPictograph>
      );
      break;

    case SelfieStep.Left:
      prompt = t('selfie.prompt.prompt-left');
      pictograph = (
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        <CenteredPictograph height={null} width={null} aria-label='stillfie left pictograph'>
          <CustomizablePictograph
            url='selfieLeftPictographUrl'
            height='selfieLeftPictographHeight'
            aria-label='stillfie left pictograph'
            themed={false}
          >
            <StillfieHint>
              <StillfieHintColumn>
                <SelfiePictograph>
                  <SelfiePictograph.Left1 />
                </SelfiePictograph>
                <StillfieHintIconContainer>
                  <IconCircleFilledCheck />
                </StillfieHintIconContainer>
                <StillfieHintText>{t('selfie.prompt.prompt-stillfie-hint-left')}</StillfieHintText>
              </StillfieHintColumn>

              <Spacer width={3} />

              <StillfieHintColumn>
                <SelfiePictograph>
                  <SelfiePictograph.Left3 />
                </SelfiePictograph>
                <StillfieHintIconContainer>
                  <IconCircleFilledX />
                </StillfieHintIconContainer>
                <StillfieHintText>{t('selfie.prompt.prompt-stillfie-hint-wrong')}</StillfieHintText>
              </StillfieHintColumn>
            </StillfieHint>
          </CustomizablePictograph>
        </CenteredPictograph>
      );
      break;

    case SelfieStep.Right:
      prompt = t('selfie.prompt.prompt-right');
      pictograph = (
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        <CenteredPictograph height={null} width={null} aria-label='stillfie right pictograph'>
          <CustomizablePictograph
            url='selfieRightPictographUrl'
            height='selfieRightPictographHeight'
            aria-label='stillfie right pictograph'
            themed={false}
          >
            <StillfieHint>
              <StillfieHintColumn>
                <SelfiePictograph>
                  <SelfiePictograph.Right1 />
                </SelfiePictograph>
                <StillfieHintIconContainer>
                  <IconCircleFilledCheck />
                </StillfieHintIconContainer>
                <StillfieHintText>{t('selfie.prompt.prompt-stillfie-hint-right')}</StillfieHintText>
              </StillfieHintColumn>

              <Spacer width={3} />

              <StillfieHintColumn>
                <SelfiePictograph>
                  <SelfiePictograph.Right3 />
                </SelfiePictograph>
                <StillfieHintIconContainer>
                  <IconCircleFilledX />
                </StillfieHintIconContainer>
                <StillfieHintText>{t('selfie.prompt.prompt-stillfie-hint-wrong')}</StillfieHintText>
              </StillfieHintColumn>
            </StillfieHint>
          </CustomizablePictograph>
        </CenteredPictograph>
      );
      break;
  }

  return (
    <>
      <Header>
        <ThemedTitle>
          <Markdown>{t('selfie.prompt.title-stillfie')}</Markdown>
        </ThemedTitle>
        <ThemedParagraph>
          <Markdown>{prompt}</Markdown>
        </ThemedParagraph>
      </Header>

      {pictograph}

      <Spacer height={3} />

      <PositionableButtonContainer>
        <CaptureOptionsContainer>
          <ButtonInputCapture
            id='selfie-prompt__button--stillfie'
            buttonType={ButtonType.Primary}
            capture='user'
            disabled={isProcessing != null}
            isPending={isProcessing === PromptOptions.UploadImage}
            handleSelectImage={handleSelectImage}
          >
            <IconCameraStyled />
          </ButtonInputCapture>
          {enabledCaptureOptions.includes(SelfieCaptureOption.DeviceHandoff) && (
            <DeviceHandoffOption
              handleDeviceHandoff={handleDeviceHandoff}
              isProcessing={isProcessing}
            />
          )}
        </CaptureOptionsContainer>
      </PositionableButtonContainer>
    </>
  );
}
